import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { 
  Building2, 
  Bot, 
  Users, 
  Cog,
  ArrowRight,
  CheckCircle,
  Sparkles
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

const solutions = [
  {
    icon: Building2,
    title: 'CRM Batiment',
    description: 'Un outil concu specifiquement pour les artisans. Pipeline commercial, devis, factures, chantiers : tout au meme endroit.',
    color: 'bg-blue-50 text-ba-primary border-blue-100',
  },
  {
    icon: Bot,
    title: 'Agents IA',
    description: '7 agents specialises qui travaillent 24h/24 : standard telephonique, emails, marketing, prospection...',
    color: 'bg-orange-50 text-ba-secondary border-orange-100',
  },
  {
    icon: Users,
    title: 'Assistants humains',
    description: 'Des assistants formés à ton métier qui comprennent tes besoins et gèrent tes tâches administratives.',
    color: 'bg-emerald-50 text-emerald-600 border-emerald-100',
  },
  {
    icon: Cog,
    title: 'Automatisation',
    description: 'Des workflows automatises pour les taches repetitives : relances, rappels, suivi des paiements...',
    color: 'bg-purple-50 text-purple-600 border-purple-100',
  },
];

const transformation = [
  'D\'artisan deborde a entrepreneur organise',
  'Des devis envoyes en quelques minutes',
  'Des clients suivis et relances automatiquement',
  'Une vision claire de ton activité',
  'Plus de temps pour ce qui compte vraiment',
];

export default function SolutionSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      className="section-premium bg-white"
      data-testid="solution-section"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <span className="inline-flex items-center gap-2 badge-premium mb-6">
            <Sparkles className="w-4 h-4" />
            La solution BatiAssist
          </span>
          <h2 className="heading-section mb-6">
            Transformez ton organisation{' '}
            <span className="text-gradient">en quelques clics</span>
          </h2>
          <p className="body-large max-w-3xl mx-auto">
            BatiAssist combine CRM metier, agents IA, assistants humains et automatisation pour toi aider à développer ton activité.
          </p>
        </motion.div>

        {/* Solutions Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-16 md:mb-20">
          {solutions.map((solution, index) => {
            const Icon = solution.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="card-premium bg-white rounded-2xl p-6 md:p-8 group"
              >
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${solution.color} border transition-transform group-hover:scale-110`}>
                  <Icon className="w-8 h-8" />
                </div>
                <h3 className="heading-card mb-3">
                  {solution.title}
                </h3>
                <p className="text-slate-500 leading-relaxed">
                  {solution.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Transformation Block */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-ba-primary rounded-3xl p-8 md:p-12 lg:p-16 text-white overflow-hidden relative"
        >
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-ba-secondary/10 rounded-full blur-3xl" />
          
          <div className="grid lg:grid-cols-2 gap-10 items-center relative z-10">
            <div>
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-6 leading-tight">
                L'objectif : transformer un artisan deborde en entrepreneur organise
              </h3>
              <p className="text-white/70 text-lg mb-8">
                Avec BatiAssist, tu passes de "je gere tout seul" a "j'ai une equipe qui m'aide".
              </p>
              <div className="space-y-4">
                {transformation.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <CheckCircle className="w-6 h-6 text-ba-success flex-shrink-0" />
                    <span className="text-white/90 text-base md:text-lg">{item}</span>
                  </motion.div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-center lg:justify-end">
              <a 
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button 
                  className="bg-ba-secondary hover:bg-ba-secondary/90 text-white font-semibold rounded-full px-10 py-7 text-lg shadow-xl hover:shadow-2xl transition-all hover:scale-105 group"
                  data-testid="solution-cta-primary"
                >
                  Decouvrir BatiAssist
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
